import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { LeadPatch, LeadWithNotes } from "../api/types";
import {
  LEAD_INTEREST_LEVELS,
  LEAD_STATUSES,
  isLeadOverdue,
  leadInterestLabel,
  leadSourceLabel,
  leadStatusBadge,
  leadStatusLabel,
} from "../lib/leads";
import { formatDate, formatDateTime } from "../lib/workorders";

export function LeadsPage() {
  const queryClient = useQueryClient();
  const [building, setBuilding] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  const listKey = ["leads", { building, status }] as const;
  const listQ = useQuery({
    queryKey: listKey,
    queryFn: () => api.leads.list({ building, status }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: LeadPatch }) => api.leads.update(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  const addNoteMut = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => api.leads.addNote(id, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  const all = listQ.data?.leads ?? [];
  const term = q.trim().toLowerCase();
  const rows = term ? all.filter((l) => l.contactName.toLowerCase().includes(term)) : all;

  return (
    <Layout title="Prospective Tenants">
      <div className="page-actions">
        <Link to="/leads/new" className="btn btn-primary btn-xl">
          + Add a prospective tenant
        </Link>
      </div>

      <div className="filters">
        <label className="field">
          <span>Search</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name..." />
        </label>
        <label className="field">
          <span>Building</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="all">All buildings</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            {LEAD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {leadStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {listQ.isLoading ? (
        <p className="sub">Loading...</p>
      ) : listQ.isError ? (
        <section className="card">
          <p>Could not load prospective tenants. Please try again.</p>
        </section>
      ) : rows.length === 0 ? (
        <section className="card">
          <p className="sub">No prospective tenants match these filters.</p>
        </section>
      ) : (
        <section className="lead-workspace">
          <div className="accordion">
            {rows.map((lead) => (
              <LeadAccordionItem
                key={lead.id}
                lead={lead}
                onPatch={(patch) => updateMut.mutateAsync({ id: lead.id, patch })}
                onAddNote={(note) => addNoteMut.mutateAsync({ id: lead.id, note })}
              />
            ))}
          </div>
          <LeadCalendar leads={rows} />
        </section>
      )}
    </Layout>
  );
}

function LeadAccordionItem({
  lead,
  onPatch,
  onAddNote,
}: {
  lead: LeadWithNotes;
  onPatch: (patch: LeadPatch) => Promise<unknown>;
  onAddNote: (note: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [draft, setDraft] = useState(() => draftFromLead(lead));
  const overdue = isLeadOverdue(lead.status, lead.followUpDate);
  const interest = draft.interestLevel ?? "warm";

  useEffect(() => {
    setDraft(draftFromLead(lead));
  }, [lead]);

  function setField<K extends keyof LeadPatch>(key: K, value: LeadPatch[K]) {
    setSaved(false);
    setSaveError(false);
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function submitNote() {
    if (!note.trim()) return;
    setAddingNote(true);
    try {
      await onAddNote(note.trim());
      setNote("");
    } finally {
      setAddingNote(false);
    }
  }

  async function saveDraft() {
    setSaving(true);
    setSaveError(false);
    try {
      await onPatch(draft);
      setSaved(true);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className={"acc-item" + (open ? " open" : "") + (overdue ? " acc-item--overdue" : "")}>
      <button className="acc-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="acc-title">{lead.contactName}</span>
        <span className="acc-meta">
          <InterestSwatch level={lead.interestLevel} />
          {lead.buildingName} -{" "}
          <span className={`badge badge-${leadStatusBadge(lead.status)}`}>
            {leadStatusLabel(lead.status)}
          </span>
          {lead.followUpDate && (
            <>
              {" "}
              - Follow up:{" "}
              <strong className={overdue ? "lead-followup-overdue" : ""}>
                {overdue ? "Overdue - " : ""}
                {formatDate(lead.followUpDate)}
              </strong>
            </>
          )}
        </span>
      </button>
      <div className="acc-body">
        <dl className="detail-list">
          <div>
            <dt>Phone</dt>
            <dd>{lead.phone ?? "-"}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{lead.email ?? "-"}</dd>
          </div>
          <div>
            <dt>Preferred move-in</dt>
            <dd>{lead.preferredMoveInTiming ?? "-"}</dd>
          </div>
          <div>
            <dt>Unit preferences</dt>
            <dd>{lead.unitPreferences ?? "-"}</dd>
          </div>
          <div>
            <dt>Inquiry source</dt>
            <dd>{leadSourceLabel(lead.inquirySource)}</dd>
          </div>
          <div>
            <dt>Tour scheduled</dt>
            <dd>{lead.tourScheduledAt ? formatDateTime(lead.tourScheduledAt) : "-"}</dd>
          </div>
          <div>
            <dt>Contact office if overdue</dt>
            <dd>{lead.contactOfficeIfOverdue ? "Yes" : "No"}</dd>
          </div>
          <div>
            <dt>Notes</dt>
            <dd>{lead.notes ?? "-"}</dd>
          </div>
        </dl>

        <div className="two-col">
          <label className="field">
            <span>Name</span>
            <input
              value={draft.contactName ?? ""}
              onChange={(e) => setField("contactName", e.target.value)}
            />
          </label>
          <label className="field">
            <span>Status</span>
            <select value={draft.status ?? "new"} onChange={(e) => setField("status", e.target.value)}>
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {leadStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input
              value={draft.phone ?? ""}
              onChange={(e) => setField("phone", e.target.value || null)}
              inputMode="tel"
            />
          </label>
          <label className="field">
            <span>Email</span>
            <input
              value={draft.email ?? ""}
              onChange={(e) => setField("email", e.target.value || null)}
              inputMode="email"
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Follow-up date</span>
            <input
              type="date"
              value={draft.followUpDate ?? ""}
              onChange={(e) => setField("followUpDate", e.target.value || null)}
            />
          </label>
          <label className="field">
            <span>Tour scheduled</span>
            <input
              type="datetime-local"
              value={draft.tourScheduledAt ?? ""}
              onChange={(e) => setField("tourScheduledAt", e.target.value || null)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Activity / interest</span>
            <span className="interest-select-wrap">
              <InterestSwatch level={interest} />
              <select
                className={`interest-select interest-${interest}`}
                value={interest}
                onChange={(e) => setField("interestLevel", e.target.value)}
              >
                {LEAD_INTEREST_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {leadInterestLabel(level)}
                  </option>
                ))}
              </select>
            </span>
          </label>
          <label className="field">
            <span>Inquiry source</span>
            <input
              value={draft.inquirySource ?? ""}
              onChange={(e) => setField("inquirySource", e.target.value || null)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Preferred move-in</span>
            <input
              value={draft.preferredMoveInTiming ?? ""}
              onChange={(e) => setField("preferredMoveInTiming", e.target.value || null)}
            />
          </label>
          <label className="field">
            <span>Unit preferences</span>
            <input
              value={draft.unitPreferences ?? ""}
              onChange={(e) => setField("unitPreferences", e.target.value || null)}
            />
          </label>
        </div>

        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={draft.contactOfficeIfOverdue ?? false}
            onChange={(e) => setField("contactOfficeIfOverdue", e.target.checked)}
          />
          Contact office if overdue
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea
            rows={3}
            value={draft.notes ?? ""}
            onChange={(e) => setField("notes", e.target.value || null)}
          />
        </label>

        <div className="form-actions">
          {saved && !saving && <span className="saved-note">Saved.</span>}
          {saveError && <span className="field-error">Could not save. Try again.</span>}
          <button
            className="btn btn-primary btn-xl"
            onClick={saveDraft}
            disabled={saving || !(draft.contactName ?? "").trim()}
          >
            {saving ? "Saving..." : "Save quick changes"}
          </button>
          <button className="btn btn-ghost btn-xl" disabled title="Email provider not configured">
            Email not configured
          </button>
        </div>

        <h2>Follow-up notes</h2>
        {lead.leadNotes.length === 0 ? (
          <p className="sub">No follow-up notes yet.</p>
        ) : (
          <ul className="timeline">
            {lead.leadNotes.map((n) => (
              <li key={n.id}>
                <strong>{formatDateTime(n.createdAt)}</strong> - {n.authorName}: {n.note}
              </li>
            ))}
          </ul>
        )}
        <div className="note-area">
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a follow-up note..."
          />
        </div>
        <div className="form-actions">
          <Link to={`/leads/${lead.id}`} className="btn btn-ghost btn-xl">
            Edit lead details
          </Link>
          <button
            className="btn btn-primary btn-xl"
            onClick={submitNote}
            disabled={!note.trim() || addingNote}
          >
            {addingNote ? "Adding..." : "Add note"}
          </button>
        </div>
      </div>
    </article>
  );
}

function draftFromLead(lead: LeadWithNotes): LeadPatch {
  return {
    contactName: lead.contactName,
    phone: lead.phone,
    email: lead.email,
    preferredMoveInTiming: lead.preferredMoveInTiming,
    unitPreferences: lead.unitPreferences,
    inquirySource: lead.inquirySource,
    status: lead.status,
    followUpDate: lead.followUpDate,
    tourScheduledAt: lead.tourScheduledAt,
    interestLevel: lead.interestLevel ?? "warm",
    contactOfficeIfOverdue: lead.contactOfficeIfOverdue,
    notes: lead.notes,
  };
}

function InterestSwatch({ level }: { level: string | null }) {
  const normalized = level ?? "warm";
  return (
    <span
      className={`interest-swatch interest-${normalized}`}
      aria-label={`${leadInterestLabel(normalized)} activity / interest`}
      title={leadInterestLabel(normalized)}
    />
  );
}

function LeadCalendar({ leads }: { leads: LeadWithNotes[] }) {
  const today = new Date();
  const [monthCursor, setMonthCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(() => today.toISOString().slice(0, 10));
  const events = useMemo(() => calendarEvents(leads), [leads]);
  const eventMap = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [events]);

  const year = monthCursor.getFullYear();
  const month = monthCursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const blanks = new Date(year, month, 1).getDay();
  const monthLabel = monthCursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const upcoming = events.filter((event) => event.date >= today.toISOString().slice(0, 10)).slice(0, 8);
  const selectedEvents = eventMap.get(selectedDate) ?? [];

  return (
    <aside className="lead-calendar card" aria-label="Prospective tenant calendar">
      <div className="calendar-head">
        <button
          className="btn btn-ghost"
          onClick={() => setMonthCursor(new Date(year, month - 1, 1))}
          aria-label="Previous month"
        >
          &lt;
        </button>
        <h2>{monthLabel}</h2>
        <button
          className="btn btn-ghost"
          onClick={() => setMonthCursor(new Date(year, month + 1, 1))}
          aria-label="Next month"
        >
          &gt;
        </button>
      </div>

      <div className="calendar-grid calendar-weekdays">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {Array.from({ length: blanks }).map((_, i) => (
          <span key={`blank-${i}`} className="calendar-day calendar-day--blank" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const date = toDateKey(year, month, day);
          const dayEvents = eventMap.get(date) ?? [];
          const isSelected = date === selectedDate;
          return (
            <button
              key={date}
              className={
                "calendar-day" +
                (dayEvents.length ? " has-events" : "") +
                (isSelected ? " selected" : "")
              }
              onClick={() => setSelectedDate(date)}
              aria-pressed={isSelected}
              aria-label={`${formatDate(date)}${dayEvents.length ? `, ${dayEvents.length} activities` : ""}`}
            >
              {day}
              {dayEvents.length > 0 && (
                <span className="calendar-count" aria-hidden="true">
                  {dayEvents.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="calendar-selected">
        <h3>{formatDate(selectedDate)}</h3>
        {selectedEvents.length === 0 ? (
          <p className="sub">No tours or follow-ups on this day.</p>
        ) : (
          <ul className="calendar-events">
            {selectedEvents.map((event) => (
              <li key={`selected-${event.kind}-${event.leadId}-${event.dateTime ?? event.date}`}>
                <Link to={`/leads/${event.leadId}`} className="calendar-event-link">
                  <strong>{event.kind === "tour" ? "Tour" : "Follow-up"}</strong>
                  <span>
                    {event.name}
                    {event.dateTime ? ` at ${formatTime(event.dateTime)}` : ""}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <h3>Upcoming</h3>
      {upcoming.length === 0 ? (
        <p className="sub">No tours or follow-ups scheduled.</p>
      ) : (
        <ul className="calendar-events">
          {upcoming.map((event) => (
            <li key={`${event.kind}-${event.leadId}-${event.dateTime ?? event.date}`}>
              <Link to={`/leads/${event.leadId}`} className="calendar-event-link">
                <strong>{formatDate(event.date)}</strong>
                <span>
                  {event.kind === "tour" ? "Tour" : "Follow-up"} - {event.name}
                  {event.dateTime ? ` at ${formatTime(event.dateTime)}` : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

type CalendarEvent = {
  leadId: number;
  name: string;
  date: string;
  dateTime: string | null;
  kind: "tour" | "follow_up";
};

function calendarEvents(leads: LeadWithNotes[]): CalendarEvent[] {
  return leads
    .flatMap((lead) => {
      const events: CalendarEvent[] = [];
      if (lead.tourScheduledAt) {
        events.push({
          leadId: lead.id,
          name: lead.contactName,
          date: lead.tourScheduledAt.slice(0, 10),
          dateTime: lead.tourScheduledAt,
          kind: "tour",
        });
      }
      if (lead.followUpDate) {
        events.push({
          leadId: lead.id,
          name: lead.contactName,
          date: lead.followUpDate,
          dateTime: null,
          kind: "follow_up",
        });
      }
      return events;
    })
    .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));
}

function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
